"use client";

import * as React from "react";
import * as Yup from "yup";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { Button, Link, TextField, Box, Typography } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useAlertStore } from "@/components/context/alert-store";
import { useFormik } from "formik";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { postRequest } from "@/utils/request";
import { magicConstants } from "@/utils/magic-constants";

export default function LoginForm({ isCheckout }: Readonly<{ isCheckout?: boolean }>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingGuest, setLoadingGuest] = useState<boolean>(false);
  const { i18n } = useLingui();
  const { setMessage } = useAlertStore();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t(i18n)`Invalid email address`)
        .required(t(i18n)`Email is required`),
      password: Yup.string().required(t(i18n)`Password is required`),
    }),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      postRequest({
        path: { name: "registerGuest" }, // Your API route
        body: {}, // If your endpoint needs guest data, pass defaultGuestData here
      }),
    onSuccess: async (data: any) => {
      if (data?.success === 0) {
        setMessage({
          text: t(i18n)`Guest registration failed.`,
          severity: "warning",
        });
        // Optionally redirect to another page or stay
      } else {
        const guestEmail = data?.data.email;
        const guestPassword = magicConstants.guestLegit;

        try {
          const result = await signIn("credentials", {
            redirect: false,
            email: guestEmail,
            password: guestPassword,
            action: "login",
            is_guest: "1",
          });

          // Invalidate queries, refresh session-relevant state
          queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
          queryClient.invalidateQueries({ queryKey: ["cart"] });

          if (result?.error) {
            setMessage({ text: result.error, severity: "warning" });
            setLoading(false);
            return;
          }

          setMessage({ text: t(i18n)`Signed in as guest.`, severity: "success" });
        } catch (error) {
          console.log(error);
          setMessage({
            text: t(i18n)`An unexpected error occurred. Please try again later.`,
            severity: "error",
          });
        } finally {
          setLoadingGuest(false);
        }
      }
    },
    onError: () => {
      setMessage({
        text: t(
          i18n,
        )`An unexpected error occurred during guest registration. Please try again later.`,
        severity: "error",
      });
    },
  });
  const handleGuestLogin = async () => {
    setLoadingGuest(true);
    return mutation.mutate();
  };

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    setMessage(null);
    try {
      const result = await signIn("credentials", {
        redirect: false,
        email: values.email,
        password: values.password,
        action: "login",
      });

      queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
      queryClient.invalidateQueries({ queryKey: ["cart"] });

      if (result?.error) {
        setMessage({ text: result.error, severity: "warning" });
        setLoading(false);
      }
    } catch (error) {
      console.error(`Next auth error: ${error}`);
      setMessage({
        text: t(i18n)`An unexpected error occurred. Please try again later.`,
        severity: "error",
      });
      setLoading(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={{ mb: "20px" }}>
        <TextField
          data-testid="login-email"
          id="email"
          name="email"
          placeholder={t(i18n)`E-Mail`}
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
        />
        {formik.touched.email && formik.errors.email ? (
          <Typography sx={{ pt: "4px" }} variant="caption" color="#fd7b7c">
            {formik.errors.email}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ mb: "20px" }}>
        <TextField
          data-testid="login-password"
          id="password"
          name="password"
          placeholder={t(i18n)`Password`}
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
        />
        {formik.touched.password && formik.errors.password ? (
          <Typography sx={{ pt: "4px" }} variant="caption" color="#fd7b7c">
            {formik.errors.password}
          </Typography>
        ) : null}
      </Box>
      <Box>
        <Link
          data-testid="forgot-password-link"
          href="/forgot-password"
          underline="always"
          color="primary.main"
          sx={{
            fontSize: "14px",
            "&:hover": {
              color: "#333",
              transition: "0.3s ease",
            },
          }}
        >
          <Trans>Forgotten password?</Trans>
        </Link>
      </Box>
      <div className="flex flex-col gap-5 pt-7">
        <Button
          data-testid="login-btn"
          type="submit"
          color="primary"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            "&.Mui-disabled": {
              backgroundColor: "primary.main",
              color: "white",
              opacity: 1,
              cursor: "not-allowed",
            },
          }}
        >
          <Trans>Login</Trans>
          <Typography component="span" className={`${loading ? "loading" : ""}`}></Typography>
        </Button>
        {isCheckout && (
          <Button
            data-testid="login-btn"
            type="submit"
            color="primary"
            fullWidth
            variant="contained"
            disabled={loadingGuest}
            onClick={handleGuestLogin}
            sx={{
              borderRadius: "6px",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "#e5f7fa", // light border like screenshot
              backgroundColor: "#e5f7fa", // light teal background
              color: "#007e8a", // darker text
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#d6f1f6",
                borderColor: "#e5f7fa",
                color: "#007e8a",
              },
              ...(loadingGuest && {
                opacity: 0.7,
                cursor: "wait",
                pointerEvents: "none",
              }),
            }}
          >
            <Trans>Continue as guest</Trans>
            <Typography
              component="span"
              className={`${loadingGuest ? "loading" : ""}`}
            ></Typography>
          </Button>
        )}
      </div>
    </form>
  );
}
